import React, { useRef, useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form } from 'react-bootstrap';

const Lesson13_UseRef = () => {
  const [count, setCount] = useState(0);
  const [inputValue, setInputValue] = useState('');
  
  // useRef examples
  const inputRef = useRef(null);
  const renderCount = useRef(0);
  const prevCount = useRef();

  useEffect(() => {
    console.log('📖 Lesson 13: useRef Hook - Loaded');
    console.log('🔍 useRef creates a mutable reference that persists across renders');
    
    // Track render count
    renderCount.current += 1;
    console.log('🔄 Component rendered', renderCount.current, 'times');

    // Store previous count
    prevCount.current = count;
  });

  const focusInput = () => {
    console.log('🎯 Focusing input element via ref');
    inputRef.current.focus();
  };

  const logRefValue = () => {
    console.log('📝 Current input ref value:', inputRef.current.value);
    console.log('🔢 Render count:', renderCount.current);
    console.log('⏮️ Previous count:', prevCount.current);
  };

  return (
    <LessonLayout 
      lessonNumber={13}
      title="useRef Hook" 
      description="DOM manipulation and persisting values without re-renders"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useRef for DOM manipulation</li>
          <li>Persisting values across renders without causing re-render</li>
          <li>Storing previous values</li>
          <li>useRef vs useState</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>DOM Manipulation with useRef</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Input Field</Form.Label>
                <Form.Control
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type something..."
                />
              </Form.Group>
              <Button onClick={focusInput} variant="primary">
                Focus Input
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Tracking Renders (No Re-render)</Card.Header>
            <Card.Body>
              <h4>Counter: {count}</h4>
              <p>Component has rendered <strong>{renderCount.current}</strong> times</p>
              <p>Previous count: <strong>{prevCount.current || 0}</strong></p>
              <Button onClick={() => setCount(count + 1)} variant="success">
                Increment Counter
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>useRef Example Code</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`// Creating a ref
const inputRef = useRef(null);

// Accessing DOM element
<input ref={inputRef} />

// Using the ref
inputRef.current.focus();
inputRef.current.value;

// Persisting value (no re-render)
const renderCount = useRef(0);
useEffect(() => {
  renderCount.current += 1;
});`}
              </pre>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>Console Logger</Card.Header>
            <Card.Body>
              <Button onClick={logRefValue} variant="info">
                Log Ref Values to Console
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h4 className="mt-4">📊 useRef vs useState</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>useState</th>
                <th>useRef</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Causes Re-render</td>
                <td>✅ Yes</td>
                <td>❌ No</td>
              </tr>
              <tr>
                <td>Persists Across Renders</td>
                <td>✅ Yes</td>
                <td>✅ Yes</td>
              </tr>
              <tr>
                <td>Mutable</td>
                <td>❌ Immutable</td>
                <td>✅ Mutable (.current)</td>
              </tr>
              <tr>
                <td>Use Case</td>
                <td>UI state that affects render</td>
                <td>DOM access, values that don't affect UI</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Common Use Cases:</strong>
        <ul className="mb-0">
          <li>Accessing DOM elements (focus, scroll, measure)</li>
          <li>Storing mutable values that don't cause re-render</li>
          <li>Keeping track of previous values</li>
          <li>Storing timer/interval IDs</li>
          <li>Uncontrolled form inputs</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson13_UseRef;
